import React from "react";
import { FrontColors } from "../../../models/FrontColors";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { Flex } from "../../BuildingBlocks/Flex";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { MaxWidth } from "../../BuildingBlocks/MaxWidth";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { TextAlign } from "../../BuildingBlocks/TextAlign";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { WeightBold } from "../../WeightBold/WeightBold";
import { FrontLink } from "../../FrontLink/FrontLink";
import { FPLandingTitle } from "../Simple/FPLandingTitle/FPLandingTitle";
import { FPSimpleLogo } from "../Simple/FPSimpleLogo/FPSimpleLogo";

/**
 * Shown to someone who is signed in but whose account is not linked to a firm.
 *
 * **When to use:** the property report page, when the session resolves to a user with
 * no introducer. It must never fall through to the sign-in form: that is the same
 * form they just completed, so offering it again produces an endless loop rather than
 * an error they can act on.
 *
 * @example
 * <FPPropertyReportNoFirmMatched email="keri.hutton@sailhomes.co.uk" />
 */
export const FPPropertyReportNoFirmMatched = (props: {
    email: string | null;
}): JSX.Element =>
    <SpacingColumn
        spacing={FrontSpacing.LARGE_1}
        justifyContent="center"
    >
        {/* LOGO */}
        <Flex justifyContent="center">
            <FPSimpleLogo width="95px" />
        </Flex>

        {/* HEADER */}
        <Flex justifyContent="center">
            <MaxWidth width="530px">
                <FPLandingTitle
                    size={60}
                    color={FrontColors.NEUTRAL_18}
                    align="center"
                >
                    You&apos;re signed in.
                </FPLandingTitle>
            </MaxWidth>
        </Flex>

        {/* EXPLANATION */}
        <Flex justifyContent="center">
            <MaxWidth width="530px">
                <Flex justifyContent="center">
                    <MaxWidth width="440px">
                        <TextColor color={FrontColors.NEUTRAL_18}>
                            <TextAlign position="center">
                                <FontQuicksand>
                                    <FontSize
                                        size={20}
                                        lineHeight={1.2}
                                    >
                                        <WeightSemiBold>
                                            <WeightBold>We can&apos;t match {props.email ? "your email" : "your account"} to a firm yet</WeightBold>, so we don&apos;t know who to log the valuation against. <WeightBold>Email the team at <FrontLink href="mailto:hello@sailhomes.co.uk?subject=Valuation tool access">hello@sailhomes.co.uk</FrontLink> and we&apos;ll link you up.</WeightBold> You won&apos;t need to sign in again.
                                        </WeightSemiBold>
                                    </FontSize>
                                </FontQuicksand>
                            </TextAlign>
                        </TextColor>
                    </MaxWidth>
                </Flex>
            </MaxWidth>
        </Flex>
    </SpacingColumn>;
